/** localStorage helpers for the Simulation Hub (bookmarks, recents, progress, notes). */
function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* non-fatal */
  }
}

const BOOKMARK_KEY = "sim_bookmarks";
const RECENT_KEY = "sim_recents";
const PROGRESS_KEY = "sim_progress";
const NOTES_KEY = "sim_notes";

export type SimRecent = { id: string; at: number };
export type SimProgress = Record<string, number>;

export function readBookmarks(): string[] {
  return read<string[]>(BOOKMARK_KEY, []);
}

export function toggleBookmark(id: string): string[] {
  const list = readBookmarks();
  const next = list.includes(id) ? list.filter((x) => x !== id) : [id, ...list];
  write(BOOKMARK_KEY, next);
  return next;
}

export function readRecents(): SimRecent[] {
  return read<SimRecent[]>(RECENT_KEY, []);
}

export function pushRecent(id: string): SimRecent[] {
  const next = [{ id, at: Date.now() }, ...readRecents().filter((r) => r.id !== id)].slice(0, 12);
  write(RECENT_KEY, next);
  return next;
}

export function readProgress(): SimProgress {
  return read<SimProgress>(PROGRESS_KEY, {});
}

export function setProgress(id: string, percent: number) {
  const all = readProgress();
  all[id] = Math.max(all[id] ?? 0, Math.min(100, Math.round(percent)));
  write(PROGRESS_KEY, all);
  return all;
}

export function readNote(id: string): string {
  return read<Record<string, string>>(NOTES_KEY, {})[id] ?? "";
}

export function saveNote(id: string, text: string) {
  const all = read<Record<string, string>>(NOTES_KEY, {});
  all[id] = text;
  write(NOTES_KEY, all);
}